import { faPlus, faPrint } from '@fortawesome/free-solid-svg-icons'
import { faFileInvoice } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid'
import { Button, Menu, MenuItem, Stack, Switch, TextField } from '@vapor/material'
import { FormItem } from 'hic-components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePasserByBillsQuery } from 'src/api/api.generated'
import CheckoutDocumentForm from 'src/app/pages/accounting/documents/checkout-document/checkout-document-form'
import { HicDataGridPro } from 'src/app/components/hic-vapor/datagrid/datagrid'
import AccountBalanceReportViewer from 'src/app/components/report-viewer/account-balance/account-balance-report-viewer'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useFormatDate from 'src/app/hooks/current-locale/use-format-date'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useInfoSnackBar from 'src/app/hooks/info-snack-bar/use-info-snack-bar'
import BillCreationModal, { BillData } from '../creation/bill-creation-modal'
import { usePasserbyBillColumns } from './passerby-bill-list-columns.hook'
import usePasserbyBillListHook from './passerby-bill-list.hook'
import DocumentReportViewer from 'src/app/components/report-viewer/documnent/document-report-viewer'

export type PasserbyBillListProp = {
  propertyId: string
  onPasserbyBillItemClick: (passerbyBillId: string) => void
}

export function PasserbyBillList(props: PasserbyBillListProp) {
  const PAGINATION_LIMIT = 10
  const PAGE_SIZE = 10
  const { propertyId } = props
  const { t } = useTranslation()
  const { formatTableCurrency } = useFormatCurrency()
  const formatDate = useFormatDate()
  const { id: tenantId, currency } = useTenant()
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)

  const [printDocumentId, setPrintDocumentId] = useState<string | null>(null)

  const [queryFiltersState, setQueryFiltersState] = useState<{ tenantId: string; propertyId: string; open?: boolean; search?: string; skip?: number; limit?: number }>({
    tenantId: tenantId,
    propertyId: props.propertyId,
    open: true,
  })

  const {
    data,
    loading: loadingBills,
    refetch: refetchPasserByBills,
  } = usePasserByBillsQuery({
    variables: {
      ...queryFiltersState,
      skip: PAGE_SIZE * page,
      limit: PAGE_SIZE,
    },
    notifyOnNetworkStatusChange: true,
  })

  //#region Menu
  const [billToEdit, setBillToEdit] = useState<BillData | undefined>(undefined) //** keep the id of the row where the menu is now open
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openMenu = Boolean(anchorEl)
  const handleOpenMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleCloseMenu = () => {
    setAnchorEl(null)
  }
  //#endregion

  //#region dialog
  const [creationDialogIsOpen, setCreationDialogIsOpen] = useState(false)
  const openCreationDialog = () => setCreationDialogIsOpen(true)
  const closeCreationDialog = (success?: boolean) => {
    setBillToEdit(undefined)
    setCreationDialogIsOpen(false)
    if (success)
      refetchPasserByBills({
        ...queryFiltersState,
        skip: PAGE_SIZE * page,
        limit: PAGE_SIZE,
      })
  }
  //#endregion dialog

  //#region account balance drawer
  const [openAccountBalance, setOpenAccountBalance] = useState(false)
  const closeAccountBalance = () => setOpenAccountBalance(false)
  //#endregion

  //#region document drawer
  const [checkoutDocumentId, setCheckoutDocumentId] = useState<null | string>(null)
  //#endregion

  const columns = usePasserbyBillColumns(
    (element, passerbyBill) => {
      setBillToEdit(passerbyBill)
      handleOpenMenuClick(element)
    },
    props.onPasserbyBillItemClick,
    setCheckoutDocumentId,
  )

  const { loading: loadingCRUD, editBill, createNewBill, deleteBill } = usePasserbyBillListHook(propertyId, { ...queryFiltersState }, PAGINATION_LIMIT, closeCreationDialog)

  useEffect(() => {
    setLoading(loadingCRUD || loadingBills)
  }, [loadingCRUD, loadingBills])

  const [selectedRowsId, setSelectedRowsId] = useState<string[]>([])
  const onSelectionModelChange = useCallback(
    (selectionModel: GridRowSelectionModel): void => {
      const newSelectedRows = selectionModel.map((id) => id.toString())
      setSelectedRowsId(newSelectedRows)
    },
    [setSelectedRowsId],
  )

  const { sm } = useBreakpoints()

  const [openDocumentDrawer, setOpenDocumentDrawer] = useState(false)
  const infoSnackBar = useInfoSnackBar()
  const showError = useErrorSnackBar()

  const accountBalanceDrawer = useMemo(() => {
    const bills =
      selectedRowsId.length > 0
        ? selectedRowsId.map((billId) => {
            return { billId: billId }
          })
        : undefined
    return <>{openAccountBalance && <AccountBalanceReportViewer bills={bills} open={openAccountBalance} setClose={closeAccountBalance} propertyId={propertyId} />}</>
  }, [selectedRowsId, openAccountBalance, propertyId])

  const presentingOpenBills = useMemo(() => queryFiltersState?.open ?? true, [queryFiltersState?.open])

  return (
    <>
      <Stack direction="column" spacing={1}>
        <Stack direction={sm ? 'row' : 'column'} justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1}>
            <FormItem fieldLabel={t('pms.booking.passerby-bill.list_filter.search')}>
              <TextField
                size="small"
                onBlur={(e) => {
                  setQueryFiltersState({
                    ...queryFiltersState,
                    search: (e.target as any).value,
                  })
                  setPage(0)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setQueryFiltersState({ ...queryFiltersState, search: (e.target as any).value })
                    setPage(0)
                    e.preventDefault()
                  }
                }}
              />
            </FormItem>
            <FormItem fieldLabel={t('pms.booking.passerby-bill.list_filter.state')}>
              <Switch
                sx={{ marginBottom: -12 }}
                edge="start"
                checked={presentingOpenBills}
                onChange={(event) => {
                  setQueryFiltersState({ ...queryFiltersState, open: event.target.checked })
                }}
              />
            </FormItem>
          </Stack>
          <Stack direction="row" spacing={1}>
            <FormItem forceLabelMargin={sm ? true : undefined}>
              <Button
                variant="outlined"
                size="small"
                disabled={!presentingOpenBills}
                onClick={() => {
                  if (selectedRowsId.length > 0) {
                    setOpenDocumentDrawer(true)
                  } else {
                    infoSnackBar(t('pms.booking.passerby-bill.select_at_least_one_bill'))
                  }
                }}
                fullWidth={false}
              >
                <Stack direction="row" spacing={1} alignItems={'center'}>
                  <FontAwesomeIcon icon={faFileInvoice} style={{ marginRight: 2 }} />
                  {t('pms.booking.passerby-bill.list-action.document')}
                </Stack>
              </Button>
            </FormItem>
            <FormItem forceLabelMargin={sm ? true : undefined}>
              <Button
                variant="outlined"
                size="small"
                disabled={!presentingOpenBills}
                onClick={() => {
                  if (selectedRowsId.length > 0) {
                    setOpenAccountBalance(true)
                  } else {
                    showError(t('pms.booking.pos.passerby-bills.select_bills_for_account_balance'))
                  }
                }}
                fullWidth={false}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <FontAwesomeIcon icon={faPrint} />
                  <p>{t('pms.booking.passerby-bill.list-action.account-balance')}</p>
                </Stack>
              </Button>
            </FormItem>
            <FormItem forceLabelMargin={sm ? true : undefined}>
              <Button variant="contained" size="small" onClick={openCreationDialog} startIcon={<FontAwesomeIcon icon={faPlus} />} fullWidth={false}>
                {t('pms.booking.passerby-bill.list-action.add-new-bill')}
              </Button>
            </FormItem>
          </Stack>
        </Stack>
        <HicDataGridPro
          loading={loading}
          localeText={{
            MuiTablePagination: {
              labelDisplayedRows: ({ from, to, count }) => `${from} - ${to} ${t('commons.of')} ${count === Number.MAX_VALUE ? t('commons.many') : count}`,
            },
          }}
          autoHeight
          pagination
          paginationModel={{page, pageSize:PAGE_SIZE}}
          paginationMode="server"
          rowCount={Number.MAX_VALUE}
          pageSizeOptions={[PAGE_SIZE]}
          onPaginationModelChange={({pageSize, page:newPage}) => {
            if (data?.passersByBills?.length || newPage < page) setPage(newPage)
          }}
          columns={presentingOpenBills ? columns.filter((col) => !col.hideable) : columns}
          checkboxSelection={presentingOpenBills}
          isRowSelectable={(params: GridRowParams) => params.row.hasProductions}
          rows={
            data?.passersByBills?.map((bill) => {
              return {
                ...bill,
                id: bill._id,
                accountHolder: bill.owner?.name,
                description: bill.description,
                creationDate: bill.creationDate ? formatDate(bill.creationDate) : '',
                total: formatTableCurrency(bill.total ?? 0, currency),
                hasProductions: (bill.productionLength || 0) > 0,
                document: bill.document,
              }
            }) ?? []
          }
          onRowSelectionModelChange={onSelectionModelChange}
        />
      </Stack>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem
          onClick={() => {
            openCreationDialog()
            handleCloseMenu()
          }}
        >
          {t('pms.booking.passerby-bill.list-action.modify')}
        </MenuItem>
      </Menu>
      <BillCreationModal
        open={creationDialogIsOpen}
        toEditBill={billToEdit}
        setOpen={(value) => (value ? openCreationDialog() : closeCreationDialog())}
        title={billToEdit ? t('pms.booking.passerby-bill.creation-dialog.title-edit') : t('pms.booking.passerby-bill.creation-dialog.title-creation')}
        confirmAction={async (passerbyBill) => {
          if (passerbyBill.billId) {
            editBill(passerbyBill.billId, passerbyBill)
          } else {
            createNewBill(passerbyBill)
          }
        }}
        cancelAction={() => closeCreationDialog()}
        deleteAction={(passerbyBillId) => {
          if ((billToEdit?.productionLength ?? 0) > 0) {
            showError(t('pms.booking.passerby-bill.cannot_delete_bill_message'))
          } else {
            deleteBill(passerbyBillId, ['PasserByBills'])
          }
        }}
      />
      <CheckoutDocumentForm //
        propertyId={propertyId}
        open={openDocumentDrawer && selectedRowsId.length > 0}
        onClose={() => setOpenDocumentDrawer(false)}
        billIds={selectedRowsId}
        productionAssignedIds={[]}
        depositDocumentIds={[]}
        onSuccess={({ documentId }) => {
          refetchPasserByBills()
          setPrintDocumentId(documentId)
        }}
        onCreditNoteSuccess={() => {
          refetchPasserByBills()
          setOpenDocumentDrawer(false)
        }}
      />

      {accountBalanceDrawer}

      {checkoutDocumentId !== null && (
        <CheckoutDocumentForm //
          propertyId={propertyId}
          open={checkoutDocumentId !== null}
          onClose={() => setCheckoutDocumentId(null)}
          documentId={checkoutDocumentId}
          onCreditNoteSuccess={(creditNoteId, manualDocumentId) => setCheckoutDocumentId(manualDocumentId ? manualDocumentId : creditNoteId)}
          onSuccess={({ documentId }) => {
            setCheckoutDocumentId(null)
            setPrintDocumentId(documentId)
          }}
        />
      )}
      {printDocumentId && (
        <DocumentReportViewer
          open={printDocumentId !== null}
          setClose={() => {
            setPrintDocumentId(null)
          }}
          documentId={printDocumentId}
          propertyId={propertyId}
        />
      )}
    </>
  )
}
